import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import PartOne from './PartOne';
import PartTwo from './PartTwo';
import PartThree from './PartThree';
import PartFour from './PartFour';
import PartFive from './PartFive';
import CertificationSuccess from '../ComponentForms/CertificationSuccess';
import ErrorMessage from '../../../components/ErrorMessage';
import LoadingSpinner from '../../../components/LoadingSpinner';
import OrganisationLogo from '../../../components/OrganisationLogo';

// ICONS
import { ReactComponent as LeftArrrow } from '../../../assets/svg/arrow-left.svg';

// TYPES
import {
	AccountExistingCertificationsCertification,
	AccountJoinOrganisationCertification,
	AccountJoinOrganisationCommand,
	DomainCertificationReviewType,
	DomainCertificationType,
	DomainProfileType,
	OrganisationFindOrganisationSummary,
	OrganisationProfileCheckProfileCheckResult
} from '../../../api/models';
import { FileType } from '../../../components/FileUpload';

export interface CertificationTypes {
	organisationRole?: string;
	authorityToWork?: {
		certificationType?: DomainCertificationType;
		state?: string;
		expiry?: string;
		certificationNumber?: string;
		createAttachments?: string[];
		attachments?: FileType[];
		userAcknowledgement?: boolean;
	}[];
	policeCheck?: {
		certificationType?: DomainCertificationType;
		date?: string;
		createAttachments?: string[];
		attachments?: FileType[];
	};
	qualificationCertificate?: {
		qualificationCertificateType?: DomainCertificationType;
		title?: string;
		date?: string;
		organisation?: string;
		createAttachments?: string[];
		attachments?: FileType[];
	}[];
	vaccinationCertificate?: {
		createAttachments?: string[];
		attachments?: FileType[];
		userAcknowledgement?: boolean;
	};
	acknowledge?: boolean;
}

interface CertificationStaffTypes {
	organisationData: OrganisationFindOrganisationSummary | undefined;
	profileData: { label?: string; value?: DomainProfileType } | undefined;
	profileCheck: OrganisationProfileCheckProfileCheckResult | undefined;
}

const CertificationStaff: React.FC<CertificationStaffTypes> = ({ organisationData, profileData, profileCheck }) => {
	// STATES
	const [certificationFormData, setCertificationFormData] = React.useState<CertificationTypes>({});

	const [formSection, setFormSection] = React.useState<string>('1');

	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [existingCertifications, setExistingCertifications] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const [existingTeacherRegistration, setExistingTeacherRegistration] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const [existingWWCCRegistration, setExistingWWCCRegistration] = React.useState<
		AccountExistingCertificationsCertification[]
	>([]);

	const [newlyCreatedOrganisationId, setNewlyCreatedOrganisationId] = React.useState<string>('');
	const [isProfileActive, setIsProfileActive] = React.useState<boolean>(false);

	const getExistingCertifications = async () => {
		setIsLoading(true);

		const response = await apibridge.getAccountExistingCertifications();

		if (!response.isError && response.result) {
			const { result = [] } = response;
			setExistingCertifications(result);

			setExistingTeacherRegistration(
				result.filter((item) => item.certificationType === DomainCertificationType.TeacherRegistration)
			);

			setExistingWWCCRegistration(
				result.filter((item) => item.certificationType === DomainCertificationType.WorkingWithChildrenCheck)
			);

			setShowError(false);
		}

		setIsLoading(false);
	};

	React.useEffect(() => {
		getExistingCertifications();
	}, []);

	// REFS
	const normalisedCertificationData = React.useRef<any[]>([]);

	// Go back to previous form section
	const goBack = () => {
		switch (formSection!) {
			case '2':
				setFormSection('1');
				break;
			case '3':
				setFormSection('2');
				break;
			case '4':
				setFormSection('3');
				break;
			case '5':
				setFormSection('4');
				break;
			default:
				break;
		}
	};

	const formatCertificationData = (certificationFormData: CertificationTypes) => {
		const authorityToWork: AccountJoinOrganisationCertification[] =
			certificationFormData?.authorityToWork?.map((form) => {
				return form.certificationType !== undefined
					? {
							certificationType: form.certificationType,
							state: form.state,
							expiry: form.expiry,
							certificationNumber: form.certificationNumber,
							createAttachments: form.createAttachments,
							userAcknowledgement: form.userAcknowledgement
					  }
					: {};
			}) || [];

		const policeCheck: AccountJoinOrganisationCertification = certificationFormData.policeCheck?.date
			? {
					certificationType: DomainCertificationType.PoliceCheck,
					date: certificationFormData.policeCheck.date,
					createAttachments: certificationFormData.policeCheck.createAttachments
			  }
			: {};

		const qualificationCertificate: AccountJoinOrganisationCertification[] =
			certificationFormData.qualificationCertificate?.map((form) => {
				return form.qualificationCertificateType
					? {
							certificationType: form.qualificationCertificateType,
							title: form.title,
							date: form.date,
							organisation: form.organisation,
							createAttachments: form.createAttachments
					  }
					: {};
			}) || [];

		const vaccinationCertificate: AccountJoinOrganisationCertification = certificationFormData.vaccinationCertificate
			?.createAttachments?.length
			? {
					certificationType: DomainCertificationType.VaccinationCertificate,
					createAttachments: certificationFormData.vaccinationCertificate?.createAttachments,
					userAcknowledgement: certificationFormData.vaccinationCertificate?.userAcknowledgement
			  }
			: {};

		normalisedCertificationData.current = [
			...authorityToWork,
			policeCheck,
			...qualificationCertificate,
			vaccinationCertificate
		]; // all the normalized certification data
	};

	const doJoin = async () => {
		if (!certificationFormData) {
			console.warn('no certification form data', certificationFormData);
			return;
		}

		formatCertificationData(certificationFormData);

		const noneEmptyData: AccountJoinOrganisationCertification[] = [];

		normalisedCertificationData.current.map((item) => Object.keys(item).length > 0 && noneEmptyData.push(item));

		const fields: AccountJoinOrganisationCommand = {
			organisationIdentifier: organisationData?.identifier,
			profileType: profileData?.value,
			staffType: certificationFormData.organisationRole,
			certifications: noneEmptyData
		};

		setIsLoading(true);

		const response = await apibridge.doJoinOrganisation(fields);

		if (response.isError) {
			setIsLoading(false);

			setShowError(true);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = {} } = response;

			setShowError(false);

			setIsLoading(false);

			setNewlyCreatedOrganisationId(result.profileId || '');

			setIsProfileActive(result.isActive || false);

			setFormSection('success');
		}
	};

	return (
		<div className="page-add-certification">
			{showError ? (
				<ErrorMessage message={formErrorMessage} />
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="header row">
									<div className="col-2">
										{formSection !== '1' && formSection !== 'success' ? (
											<button onClick={goBack} className="back-button">
												<LeftArrrow />
												Back
											</button>
										) : null}
									</div>

									<div className="section-header col-8">
										{formSection === 'success' ? null : (
											<>
												<OrganisationLogo src={organisationData && organisationData.image} />

												<h2 className="h1">{organisationData?.name}</h2>

												{profileCheck?.isPastMember ? (
													<p className="sub-purple-text mtop-20">
														We've detected you've had a past role with this organisation. We'll reinstate your account
														with this organisation. Please select your new role and update any required documents.
													</p>
												) : (
													<p className="sub-grey-text">Please provide additional certification information.</p>
												)}

												<p className="sub-grey-text">Part {formSection} of 5</p>
											</>
										)}
									</div>

									<div className="col-2"></div>
								</div>

								<section>
									{formSection === '1' ? (
										<PartOne
											staffTypes={organisationData && organisationData.staffTypes}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '2' ? (
										<PartTwo
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(item) => item.certificationType === DomainCertificationReviewType.WorkingWithChildrenCheck
												)
											}
											existingWWCCRegistration={existingWWCCRegistration}
											existingTeacherRegistration={existingTeacherRegistration}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '3' ? (
										<PartThree
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(cert) => cert.reviewType === DomainCertificationReviewType.PoliceCheck
												)
											}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '4' ? (
										<PartFour
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(cert) => cert.reviewType === DomainCertificationReviewType.Qualification
												)
											}
											certificationData={certificationFormData}
											setFormSection={setFormSection}
											setCertificationFormData={setCertificationFormData}
										/>
									) : formSection === '5' ? (
										<PartFive
											existingCertifications={
												existingCertifications &&
												existingCertifications.filter(
													(cert) => cert.reviewType === DomainCertificationReviewType.Vaccination
												)
											}
											certificationData={certificationFormData}
											setCertificationFormData={setCertificationFormData}
											loading={isLoading}
											formErrorMessage={formErrorMessage}
											setFormSection={setFormSection}
											doJoin={doJoin}
										/>
									) : formSection === 'success' ? (
										<CertificationSuccess
											organisationName={organisationData?.name}
											isActive={isProfileActive}
											newlyCreatedOrganisationId={newlyCreatedOrganisationId}
										/>
									) : null}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default CertificationStaff;
